import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import {
  HANDLEBARS_INLINE_ATTR,
  HANDLEBARS_NODE_NAME,
  HANDLEBARS_TEMPLATE_ATTR,
} from "./constants";
import { HandlebarsNodeView } from "./handlebars-node";

export interface HandlebarsNodeOptions {
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Inline, atomic node representing any handlebars construct that is not a bare
 * data key (conditionals, helpers, loops). Stateless: stores the raw handlebars
 * template and evaluates it live via the dynamic node context. Opaque/read-only —
 * its inner content is not editable in the document.
 */
export const HandlebarsNode = Node.create<HandlebarsNodeOptions>({
  name: HANDLEBARS_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      [HANDLEBARS_TEMPLATE_ATTR]: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute(`data-${HANDLEBARS_TEMPLATE_ATTR}`) ?? "",
        renderHTML: (attributes) => ({
          [`data-${HANDLEBARS_TEMPLATE_ATTR}`]:
            attributes[HANDLEBARS_TEMPLATE_ATTR],
        }),
      },
      [HANDLEBARS_INLINE_ATTR]: {
        default: true,
        parseHTML: (element) =>
          element.getAttribute(`data-${HANDLEBARS_INLINE_ATTR}`) !== "false",
        renderHTML: (attributes) => ({
          [`data-${HANDLEBARS_INLINE_ATTR}`]: String(
            attributes[HANDLEBARS_INLINE_ATTR]
          ),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${HANDLEBARS_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { "data-type": HANDLEBARS_NODE_NAME },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HandlebarsNodeView);
  },
});

export default HandlebarsNode;
